import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type Asset } from "../api/rest";
import { useTelemetryStore, getDisplayStatus, relativeTime } from "../stores/telemetryStore";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { useNow } from "../hooks/useNow";
import { SplitView } from "../app/layout/SplitView";
import { InspectorPanel } from "../panels/InspectorPanel";
import styles from "./FleetPage.module.css";

export function FleetPage() {
  const SITE_ID = useSiteId();
  const WAREHOUSE_ID = useWarehouseId();
  const { t } = useTranslation();
  // Re-render every 5 s so relative times and stale status stay fresh (shared clock, ADR 136).
  useNow(5_000);

  const live  = useTelemetryStore((s) => s.forklifts);
  const select = useTelemetryStore((s) => s.select);
  const selectedId = useTelemetryStore((s) => s.selectedAssetId);
  const setRoster = useTelemetryStore((s) => s.setRoster);

  const { data: roster, isLoading, isError } = useQuery<Asset[]>({
    queryKey: ["fleet", SITE_ID, WAREHOUSE_ID],
    queryFn: () => api.listAssets(SITE_ID, WAREHOUSE_ID),
    retry: 0,
  });

  // Seed store with full roster so Network page count and offline status are correct.
  useEffect(() => {
    if (roster) setRoster(roster);
  }, [roster, setRoster]);

  const rows = (roster ?? []).map((f) => {
    const lp = live[f.id];
    return {
      id: f.id,
      label: f.label,
      status: lp ? getDisplayStatus(lp) : "offline" as const,
      speed:  lp?.speed     ?? 0,
      battery: lp?.battery  ?? 0,
      lastSeenMs: lp?.lastSeenMs ?? 0,
    };
  });

  const table = (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t("common.col.id")}</th>
            <th>{t("common.col.status")}</th>
            <th>{t("common.col.speed")}</th>
            <th>{t("common.col.battery")}</th>
            <th>{t("common.col.lastSeen")}</th>
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 3 }, (_, i) => <SkeletonRow key={i} />)
            : isError
              ? <tr><td colSpan={5} className={styles.error}>{t("fleet.error")}</td></tr>
              : rows.length > 0
                ? rows.map((r) => (
                    <tr key={r.id} onClick={() => select(r.id)} tabIndex={0}
                        aria-selected={r.id === selectedId}
                        className={r.id === selectedId ? styles.rowSelected : undefined}>
                      <td className={styles.mono}>{r.id}</td>
                      <td>
                        <span className={`${styles.pill} ${styles[`pill_${r.status}`]}`}>
                          {t(`status.${r.status}`)}
                        </span>
                      </td>
                      <td className={styles.mono}>{r.speed.toFixed(2).replace(".", ",")} m/s</td>
                      <td className={styles.mono}>{Math.round(r.battery * 100)}%</td>
                      <td className={styles.mono}>{relativeTime(r.lastSeenMs)}</td>
                    </tr>
                  ))
                : <tr><td colSpan={5} className={styles.empty}>{t("fleet.page.empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={`${styles.page} ${styles.wide}`}>
      <h1 className={styles.h1}>{t("fleet.title")}</h1>
      <p className={styles.sub}>
        {isLoading
          ? <span className="skeleton" style={{ width: 80, height: 16 }} />
          : t("fleet.count", { count: rows.length })}
      </p>

      <SplitView list={table} detail={<InspectorPanel />} detailOpen={!!selectedId}
                 detailLabel={t("a11y.inspector")} />
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[90, 70, 60, 45, 80].map((w, i) => (
        <td key={i}>
          <span className="skeleton" style={{ width: w, height: 14, display: "block" }} />
        </td>
      ))}
    </tr>
  );
}
